// src/pages/AdminCashGames.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAt,
  endAt,
  updateDoc,
  where,
  deleteDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const COPPER = "#B87333";

const STAKES = [
  { key: "0.25/0.50", label: "$0.25/$0.50", sb: 0.25, bb: 0.5, bomb: 1 },
  { key: "0.50/1", label: "$0.50/$1", sb: 0.5, bb: 1, bomb: 2 },
  { key: "1/2", label: "$1/$2", sb: 1, bb: 2, bomb: 5 },
];

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function clamp(n, min, max) {
  const x = Number(n);
  if (Number.isNaN(x)) return min;
  return Math.max(min, Math.min(max, x));
}

function toIntDollar(n) {
  const x = Math.round(Number(n || 0));
  return Number.isFinite(x) ? x : 0;
}

function seatsToMap(seats) {
  const arr = Array.isArray(seats) ? seats : [];
  const m = new Map();
  arr.forEach((s) => m.set(Number(s.seat), s));
  return m;
}

function seatsToGrid(seats) {
  const map = seatsToMap(seats);
  return Array.from({ length: 9 }, (_, i) => map.get(i + 1) || { seat: i + 1, empty: true });
}

function msToMMSS(ms) {
  const total = Math.max(Math.floor(ms / 1000), 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function beep(freq = 880, durationMs = 250, volume = 0.05) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.value = volume;

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    setTimeout(() => {
      osc.stop();
      ctx.close().catch(() => {});
    }, durationMs);
  } catch {
    // ignore
  }
}

function computeBuyInBounds(stakesKey) {
  const s = STAKES.find((x) => x.key === stakesKey);
  const bb = Number(s?.bb || 0);
  const minBuyIn = toIntDollar(40 * bb);
  const maxBuyIn = toIntDollar(100 * bb);
  return { minBuyIn, maxBuyIn };
}

function formatTableDisplayName(stakesLabel, tableNumber) {
  return `${stakesLabel} — Table ${tableNumber}`;
}

function isDue(table) {
  if (!table?.bombPotsEnabled) return false;
  if (!table?.bombPotNextDueAt?.toMillis) return false;
  return table.bombPotNextDueAt.toMillis() <= Date.now();
}

function isTimerRunning(table) {
  return !!(table?.bombPotsEnabled && table?.bombPotNextDueAt?.toMillis);
}

function normalizeCounterDocId(stakesKey) {
  return String(stakesKey || "").trim().replaceAll("/", "_").replaceAll(".", "_");
}

// ---------- Avatar helpers ----------
function SeatAvatarMini({ seat }) {
  const name = seat?.name || seat?.email || "";
  const url = seat?.profilePhotoUrl || "";

  if (url) {
    return (
      <img
        src={url}
        alt={name ? `${name} avatar` : "avatar"}
        className="h-9 w-9 rounded-full object-cover border border-zinc-700"
        loading="lazy"
      />
    );
  }

  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const initials = ((parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1]?.[0] : "")).toUpperCase();

  return (
    <div
      className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold border"
      style={{
        borderColor: "rgba(184,115,51,0.22)",
        background: "rgba(184,115,51,0.10)",
        color: COPPER,
      }}
      title={name || "Player"}
      aria-hidden="true"
    >
      {initials || "—"}
    </div>
  );
}
function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (first + last).toUpperCase();
}

function TinyAvatar({ name, url, size = 26 }) {
  const safeName = String(name || "").trim();
  const photoUrl = String(url || "").trim();

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={safeName ? `${safeName} avatar` : "avatar"}
        className="rounded-full object-cover border border-zinc-700"
        style={{ width: size, height: size, boxShadow: "0 0 0 2px rgba(184,115,51,0.10)" }}
        loading="lazy"
      />
    );
  }

  const initials = initialsFromName(safeName);
  return (
    <div
      className="rounded-full flex items-center justify-center text-[11px] font-bold border"
      style={{
        width: size,
        height: size,
        borderColor: "rgba(184,115,51,0.22)",
        background: "rgba(184,115,51,0.10)",
        color: COPPER,
        flex: "0 0 auto",
      }}
      title={safeName || "No player"}
      aria-hidden="true"
    >
      {initials || "—"}
    </div>
  );
}

function normalizeMember(docId, data) {
  const d = data || {};
  const name = d.name || d.displayName || d.email || docId;
  const email = d.email || "";
  const profilePhotoUrl = d.profilePhotoUrl || ""; // ✅ key addition

  return {
    id: docId,
    uid: docId,
    name,
    email,
    profilePhotoUrl: d.profilePhotoUrl || "", // ✅ ADD THIS
    status: d.status || "",
    role: d.role || "member",
    nameLower: d.nameLower || String(name || "").toLowerCase(),
    emailLower: d.emailLower || String(email || "").toLowerCase(),
  };
}

function normalizeChipSet(docId, data) {
  const d = data || {};
  const name = String(d.name || docId);
  const chips = Array.isArray(d.chips) ? d.chips : [];
  const cleaned = chips
    .map((c) => ({
      value: Number(c?.value || 0),
      label: String(c?.label || ""),
      color: String(c?.color || "").trim(),
    }))
    .filter((c) => Number.isFinite(c.value) && c.value > 0)
    .sort((a, b) => a.value - b.value);

  return { id: docId, name, chips: cleaned };
}

function ChipsIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: "block" }}>
      <path
        d="M12 22c4.97 0 9-1.79 9-4V6c0-2.21-4.03-4-9-4S3 3.79 3 6v12c0 2.21 4.03 4 9 4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M21 10c0 2.21-4.03 4-9 4s-9-1.79-9-4" stroke="currentColor" strokeWidth="2" />
      <path d="M21 14c0 2.21-4.03 4-9 4s-9-1.79-9-4" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export default function AdminCashGames() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [tables, setTables] = useState([]);
  const [createStakesKey, setCreateStakesKey] = useState(STAKES[0].key);
  const [creating, setCreating] = useState(false);

  const [members, setMembers] = useState([]);
  const [chipSets, setChipSets] = useState([]);

  const chipSetsById = useMemo(() => {
    const m = new Map();
    chipSets.forEach((cs) => m.set(cs.id, cs));
    return m;
  }, [chipSets]);

  // chip selection per table (UI)
  const [chipPickByTable, setChipPickByTable] = useState({});

  // used to show a subtle “Saved” state per table after chip selection change
  const [chipSaveState, setChipSaveState] = useState({}); // tableId -> "saving" | "saved" | "error"

  // timers
  const [now, setNow] = useState(Date.now());
  const beepedRef = useRef(new Set());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // admin gate
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        const admin = snap.exists() && snap.data()?.role === "admin";
        setIsAdmin(admin);
        setChecking(false);

        if (!admin) navigate("/dashboard");
      } catch {
        setIsAdmin(false);
        setChecking(false);
        navigate("/dashboard");
      }
    };
    run();
  }, [navigate]);

  // tables live feed
  useEffect(() => {
    if (!isAdmin) return;

    const ref = collection(db, "cashTables");
    const qRef = query(ref, orderBy("stakesKey", "asc"), orderBy("tableNumber", "asc"));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTables(list);

        // keep chip selector in sync with DB values
        setChipPickByTable((prev) => {
          const next = { ...prev };
          const ids = new Set(list.map((t) => t.id));

          Object.keys(next).forEach((k) => {
            if (!ids.has(k)) delete next[k];
          });

          list.forEach((t) => {
            const dbVal = t.chipSetId || "";
            if (next[t.id] == null) next[t.id] = dbVal;
          });

          return next;
        });
      },
      (err) => console.error("Admin cash tables snapshot error:", err)
    );

    return () => unsub();
  }, [isAdmin]);

  // Active members cache (fallback)
  useEffect(() => {
    if (!isAdmin) return;

    const ref = collection(db, "users");
    const qRef = query(ref, where("status", "==", "active"), orderBy("name", "asc"), limit(500));

    const unsub = onSnapshot(
      qRef,
      (snap) => setMembers(snap.docs.map((d) => normalizeMember(d.id, d.data()))),
      (err) => console.error("Active users snapshot error:", err)
    );

    return () => unsub();
  }, [isAdmin]);

  // chip sets
  useEffect(() => {
    if (!isAdmin) return;

    const ref = collection(db, "chipSets");
    const qRef = query(ref, orderBy("name", "asc"));

    const unsub = onSnapshot(
      qRef,
      (snap) => setChipSets(snap.docs.map((d) => normalizeChipSet(d.id, d.data()))),
      (err) => console.error("Chip sets snapshot error:", err)
    );

    return () => unsub();
  }, [isAdmin]);

  // beep + animation trigger when a table becomes due
  useEffect(() => {
    for (const t of tables) {
      const due = isDue(t);
      if (!due) {
        beepedRef.current.delete(t.id);
        continue;
      }
      if (!beepedRef.current.has(t.id)) {
        beep(880, 260, 0.06);
        beepedRef.current.add(t.id);
      }
    }
  }, [tables, now]);

  /**
   * Build global set of seated member uids across ALL tables.
   * Used to hide them from search results and to enforce single seat.
   */
  const globallySeatedUids = useMemo(() => {
    const set = new Set();
    tables.forEach((t) => {
      const seats = Array.isArray(t.seats) ? t.seats : [];
      seats.forEach((s) => {
        const uid = String(s?.uid || "").trim();
        if (uid) set.add(uid);
      });
    });
    return set;
  }, [tables]);

  // strict: hide anyone seated anywhere
  const excludedUidsForSeatSearch = useMemo(() => globallySeatedUids, [globallySeatedUids]);

  const createTable = async () => {
    if (!isAdmin) return;
    setCreating(true);

    const stake = STAKES.find((s) => s.key === createStakesKey) || STAKES[0];
    const { minBuyIn, maxBuyIn } = computeBuyInBounds(stake.key);

    try {
      await runTransaction(db, async (tx) => {
        const countersDocId = normalizeCounterDocId(stake.key);
        const counterRef = doc(db, "cashTableCounters", countersDocId);
        const counterSnap = await tx.get(counterRef);

        const currentNext = counterSnap.exists() ? Number(counterSnap.data()?.next || 1) : 1;
        const tableNumber = currentNext;

        tx.set(counterRef, { stakesKey: stake.key, next: tableNumber + 1 }, { merge: true });

        const displayName = formatTableDisplayName(stake.label, tableNumber);
        const newDocRef = doc(collection(db, "cashTables"));

        tx.set(newDocRef, {
          stakesKey: stake.key,
          stakesLabel: stake.label,
          tableNumber,
          displayName,

          sb: stake.sb,
          bb: stake.bb,

          minBuyIn,
          maxBuyIn,

          seats: [],

          // Chip set (assigned later)
          chipSetId: "",
          chipSetName: "",
          chipSetDenoms: [],
          chipSetChips: [],

          // Bomb pots
          bombPotsEnabled: false,
          bombPotMinutes: 0,
          bombPotAmount: stake.bomb,
          bombPotNextDueAt: null,
          bombPotLastConfirmedAt: null,

          status: "open",

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (err) {
      console.error("Create table failed:", err);
      alert("Could not create table. Check permissions.");
    }

    setCreating(false);
  };

  const updateTable = async (tableId, patch) => {
    try {
      const ref = doc(db, "cashTables", tableId);
      await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error("Update table failed:", e);
      alert("Update failed.");
    }
  };

  const removeTable = async (table) => {
    if (!isAdmin) return;

    const label = table?.displayName || "this table";
    const hasPlayers = Array.isArray(table?.seats) && table.seats.length > 0;

    const confirmMsg = hasPlayers
      ? `Remove ${label}? This will DELETE the table and clear all seated players.`
      : `Remove ${label}? This will DELETE the table.`;

    const ok = window.confirm(confirmMsg);
    if (!ok) return;

    try {
      beepedRef.current.delete(table.id);
      await deleteDoc(doc(db, "cashTables", table.id));
    } catch (e) {
      console.error("Remove table failed:", e);
      alert("Could not remove table. Check permissions.");
    }
  };

  const clearSeat = async (table, seatNumber) => {
    const current = Array.isArray(table.seats) ? table.seats : [];
    const next = current.filter((s) => Number(s.seat) !== Number(seatNumber));
    await updateTable(table.id, { seats: next });
  };

  /**
   * Hard safety: block seating if member is seated at ANY other table.
   * ✅ Also writes profilePhotoUrl into seat object.
   */
  const setSeat = async (table, seatNumber, { memberUid, buyIn, memberLabel, profilePhotoUrl }) => {
    const stake = STAKES.find((s) => s.key === table.stakesKey);
    const payloadProfilePhotoUrl = String(profilePhotoUrl || "").trim();
    const { minBuyIn, maxBuyIn } = computeBuyInBounds(table.stakesKey);

    const uid = String(memberUid || "").trim();
    if (!uid) return alert("Select a member.");

    // ✅ Block if seated elsewhere
    if (globallySeatedUids.has(uid)) {
      let whereTxt = "";
      for (const tb of tables) {
        const seats = Array.isArray(tb.seats) ? tb.seats : [];
        const hit = seats.find((s) => String(s?.uid || "").trim() === uid);
        if (hit) {
          whereTxt = ` They are already seated at "${tb.displayName || tb.stakesLabel || "a table"}" (seat ${hit.seat}).`;
          break;
        }
      }
      return alert(`That member is already seated at a cash game table.${whereTxt}`);
    }

    // Prefer local cache for display, but allow assign from typeahead result
    const member =
      members.find((m) => m.uid === uid || m.id === uid) || {
        uid,
        id: uid,
        name: memberLabel || uid,
        email: "",
        status: "active",
        profilePhotoUrl: payloadProfilePhotoUrl || "", // ✅ we’ll define this
      };

    if (String(member.status || "").toLowerCase() !== "active") {
      return alert("That member is not active.");
    }

    let bi = toIntDollar(buyIn);
    bi = clamp(bi, minBuyIn, maxBuyIn);
    bi = toIntDollar(bi);

    const current = Array.isArray(table.seats) ? table.seats : [];
    const m = seatsToMap(current);

    // prevent duplicate seating of same member at same table (extra)
    const alreadySeatedSame = Array.from(m.values()).some((s) => String(s.uid || "") === uid);
    if (alreadySeatedSame) return alert("That member is already seated at this table.");

    m.set(seatNumber, {
      seat: seatNumber,
      uid,
      name: member.name || member.email || uid,
      email: member.email || "",
      buyIn: bi,
      addedAt: Date.now(),
      profilePhotoUrl: member.profilePhotoUrl || payloadProfilePhotoUrl || "",
    });

    const nextSeats = Array.from(m.values()).sort((a, b) => Number(a.seat) - Number(b.seat));

    await updateTable(table.id, {
      seats: nextSeats,
      bombPotAmount: stake?.bomb ?? table.bombPotAmount ?? 0,
      minBuyIn,
      maxBuyIn,
    });
  };

  /**
   * Apply chip set to a table (auto-saves on dropdown change)
   */
  const applyChipSetById = async (table, chipSetId) => {
    if (!isAdmin) return;

    const tableId = table.id;
    const chosenId = String(chipSetId || "");

    setChipSaveState((p) => ({ ...p, [tableId]: "saving" }));

    try {
      if (!chosenId) {
        await updateTable(tableId, {
          chipSetId: "",
          chipSetName: "",
          chipSetDenoms: [],
          chipSetChips: [],
        });

        setChipSaveState((p) => ({ ...p, [tableId]: "saved" }));
        setTimeout(() => setChipSaveState((p) => ({ ...p, [tableId]: "" })), 800);
        return;
      }

      const cs = chipSetsById.get(chosenId);
      if (!cs) throw new Error("Chip set not found");

      const chipSetChips = (Array.isArray(cs.chips) ? cs.chips : [])
        .map((c) => ({ value: Number(c.value), color: String(c.color || "").trim() }))
        .filter((c) => Number.isFinite(c.value) && c.value > 0)
        .sort((a, b) => a.value - b.value);

      const chipSetDenoms = chipSetChips.map((c) => c.value);

      await updateTable(tableId, {
        chipSetId: cs.id,
        chipSetName: cs.name,
        chipSetDenoms,
        chipSetChips,
      });

      setChipSaveState((p) => ({ ...p, [tableId]: "saved" }));
      setTimeout(() => setChipSaveState((p) => ({ ...p, [tableId]: "" })), 800);
    } catch (e) {
      console.error("Apply chip set failed:", e);
      setChipSaveState((p) => ({ ...p, [tableId]: "error" }));
      setTimeout(() => setChipSaveState((p) => ({ ...p, [tableId]: "" })), 1400);
      alert("Could not apply chip set. Check permissions / chip set.");
    }
  };

  const toggleBombPots = async (table, enabled) => {
    const stake = STAKES.find((s) => s.key === table.stakesKey);
    const bombPotAmount = stake?.bomb ?? table.bombPotAmount ?? 0;

    if (!enabled) {
      await updateTable(table.id, {
        bombPotsEnabled: false,
        bombPotMinutes: 0,
        bombPotAmount,
        bombPotNextDueAt: null,
        bombPotLastConfirmedAt: serverTimestamp(),
      });
      beepedRef.current.delete(table.id);
      return;
    }

    const existingMinutes = Number(table.bombPotMinutes || 0);
    const safeMinutes = existingMinutes > 0 ? existingMinutes : 10;

    await updateTable(table.id, {
      bombPotsEnabled: true,
      bombPotMinutes: safeMinutes,
      bombPotAmount,
      bombPotNextDueAt: null,
      bombPotLastConfirmedAt: serverTimestamp(),
    });
  };

  const setBombMinutes = async (table, minutes) => {
    const m = Math.max(Number(minutes || 0), 0);
    await updateTable(table.id, { bombPotMinutes: m });
  };

  const startBombTimer = async (table) => {
    if (!table.bombPotsEnabled) return;
    const minutes = Number(table.bombPotMinutes || 0);
    if (!minutes || minutes <= 0) return alert("Set bomb pot frequency (minutes) first.");

    const nextDueAt = new Date(Date.now() + minutes * 60 * 1000);
    await updateTable(table.id, {
      bombPotNextDueAt: nextDueAt,
      bombPotLastConfirmedAt: serverTimestamp(),
    });
    beepedRef.current.delete(table.id);
  };

  const confirmBombPot = async (table) => {
    if (!table.bombPotsEnabled) return;

    const minutes = Number(table.bombPotMinutes || 0);
    if (!minutes || minutes <= 0) return alert("Set bomb pot frequency (minutes) first.");

    const nextDueAt = new Date(Date.now() + minutes * 60 * 1000);

    await updateTable(table.id, {
      bombPotLastConfirmedAt: serverTimestamp(),
      bombPotNextDueAt: nextDueAt,
    });

    beepedRef.current.delete(table.id);
  };

  if (checking) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center text-zinc-300">Checking access...</div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 text-zinc-300">Admin access required.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: COPPER }}>
            Admin — Cash Games
          </h2>
          <p className="text-zinc-400 mt-2">Create tables, assign seats (active members only), apply chip sets, and manage bomb pots.</p>
        </div>
      </div>

      {/* Create table */}
      <div className="rounded-xl border bg-zinc-950 p-4 mb-6" style={{ borderColor: "rgba(184,115,51,0.22)" }}>
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div className="min-w-[240px]">
            <label className="block text-zinc-300 text-sm mb-1">Stakes</label>
            <select className="w-full p-3 bg-zinc-800 text-zinc-100 rounded" value={createStakesKey} onChange={(e) => setCreateStakesKey(e.target.value)}>
              {STAKES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-zinc-500 mt-2">Buy-in rule: min 40x BB, max 100x BB, $1 increments.</p>
          </div>

          <button
            onClick={createTable}
            disabled={creating}
            className={`px-4 py-3 rounded font-semibold ${
              creating ? "bg-zinc-700 text-zinc-300 cursor-not-allowed" : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
            }`}
          >
            {creating ? "Creating..." : "+ Create Table"}
          </button>
        </div>
      </div>

      {tables.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 text-zinc-300">No cash game tables yet.</div>
      ) : (
        <div className="grid gap-4">
          {tables.map((t) => {
            const seats = seatsToGrid(t.seats);
            const due = isDue(t);

            const stake = STAKES.find((s) => s.key === t.stakesKey);
            const { minBuyIn, maxBuyIn } = computeBuyInBounds(t.stakesKey);

            const remainingMs = isTimerRunning(t) && t.bombPotNextDueAt?.toMillis ? t.bombPotNextDueAt.toMillis() - now : null;

            const timerText = t.bombPotsEnabled && remainingMs != null ? (remainingMs <= 0 ? "DUE" : msToMMSS(remainingMs)) : "—";

            const chipState = chipSaveState[t.id] || "";
            const chipStateText =
              chipState === "saving" ? "Saving…" : chipState === "saved" ? "Saved ✓" : chipState === "error" ? "Error" : "";

            return (
              <div
                key={t.id}
                className={`rounded-xl border bg-zinc-950 p-4 transition-all ${due ? "animate-pulse" : ""}`}
                style={{
                  borderColor: due ? "rgba(184,115,51,0.65)" : "rgba(184,115,51,0.22)",
                  boxShadow: due ? "0 0 24px rgba(184,115,51,0.25)" : "none",
                }}
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold truncate" style={{ color: COPPER }}>
                      {t.displayName || formatTableDisplayName(t.stakesLabel || "Table", t.tableNumber)}
                    </h3>

                    <p className="text-zinc-400 mt-1 text-sm">
                      Stakes: <span className="text-zinc-200 font-semibold">{t.stakesLabel || "—"}</span>
                      {" • "}
                      Buy-in: <span className="text-zinc-200 font-semibold">{money(t.minBuyIn ?? minBuyIn)}–{money(t.maxBuyIn ?? maxBuyIn)}</span>
                      {stake ? (
                        <span className="text-zinc-500">
                          {" "}
                          • Bomb Pot Amount: <span className="text-zinc-200 font-semibold">{money(stake.bomb)}</span>
                        </span>
                      ) : null}
                    </p>

                    <p className="text-zinc-500 mt-1 text-sm">
                      Chip Set: <span className="text-zinc-200 font-semibold">{t.chipSetName ? t.chipSetName : "Not set"}</span>
                    </p>

                    {t.bombPotsEnabled && (
                      <div
                        className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm"
                        style={{ borderColor: "rgba(184,115,51,0.40)", background: "rgba(184,115,51,0.10)" }}
                      >
                        <span className="font-semibold text-zinc-100">💣 Bomb Pots ON</span>
                        {due ? <span className="text-zinc-100 font-bold">• DUE</span> : null}
                      </div>
                    )}
                  </div>

                  {/* Remove Table — top right */}
                  <button
                    onClick={() => removeTable(t)}
                    className="px-4 py-2 rounded font-semibold bg-red-600 hover:bg-red-700 text-white shrink-0"
                    title="Remove this table (deletes it)"
                  >
                    Remove Table
                  </button>
                </div>

                {/* Chip set row (auto-save on change) */}
                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[260px]">
                    <label className="block text-xs text-zinc-500 mb-1">Chip Set</label>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-flex items-center justify-center h-10 w-10 rounded border"
                        style={{ borderColor: "rgba(184,115,51,0.20)", background: "rgba(184,115,51,0.06)", color: COPPER }}
                        title="Chip set"
                      >
                        <ChipsIcon size={16} />
                      </span>

                      <select
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={chipPickByTable[t.id] ?? (t.chipSetId || "")}
                        onChange={(e) => {
                          const val = e.target.value;
                          setChipPickByTable((prev) => ({ ...prev, [t.id]: val }));
                          applyChipSetById(t, val);
                        }}
                      >
                        <option value="">(none)</option>
                        {chipSets.map((cs) => (
                          <option key={cs.id} value={cs.id}>
                            {cs.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {chipStateText ? (
                      <div className={`mt-1 text-xs ${chipState === "error" ? "text-red-400" : chipState === "saving" ? "text-zinc-400" : "text-green-400"}`}>
                        {chipStateText}
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Bomb pots row */}
                <div className="mt-4 flex flex-wrap items-end gap-3 justify-between">
                  <div className="flex flex-wrap items-end gap-3">
                    <button
                      onClick={() => toggleBombPots(t, !t.bombPotsEnabled)}
                      className={`px-4 py-2 rounded font-semibold ${
                        t.bombPotsEnabled ? "bg-green-700 hover:bg-green-800 text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                      }`}
                    >
                      {t.bombPotsEnabled ? "Bomb Pots: ON" : "Enable Bomb Pots"}
                    </button>

                    <div className="min-w-[180px]">
                      <label className="block text-xs text-zinc-500 mb-1">Frequency (minutes)</label>
                      <input
                        type="number"
                        min={0}
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={Number(t.bombPotMinutes || 0)}
                        onChange={(e) => setBombMinutes(t, e.target.value)}
                        disabled={!t.bombPotsEnabled}
                      />
                    </div>

                    <button
                      onClick={() => startBombTimer(t)}
                      disabled={!t.bombPotsEnabled || isTimerRunning(t)}
                      className={`px-4 py-2 rounded font-semibold ${
                        t.bombPotsEnabled && !isTimerRunning(t)
                          ? "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                          : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                      }`}
                      title="Start the bomb pot timer (does not auto-start on enable)"
                    >
                      Start Timer
                    </button>

                    <button
                      onClick={() => confirmBombPot(t)}
                      disabled={!t.bombPotsEnabled || !due}
                      className={`px-4 py-2 rounded font-semibold ${
                        t.bombPotsEnabled && due ? "bg-red-600 hover:bg-red-700 text-white" : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                      }`}
                      title="Admin confirms bomb pot is happening now (advances timer)"
                    >
                      Confirm
                    </button>
                  </div>

                  <div className="min-w-[160px] text-right">
                    <div className="text-xs text-zinc-500">Timer</div>
                    <div className="text-lg font-bold text-zinc-100">{timerText}</div>
                    <p className="text-[11px] text-zinc-500 mt-1">Sound may require one click first.</p>
                  </div>
                </div>

                {/* Seats grid */}
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {seats.map((s) => {
                    const occupied = !s.empty && (!!s.uid || !!s.name);
                    const displayName = s.name || "—";

                    return (
                      <div
                        key={s.seat}
                        className="rounded-lg border p-3"
                        style={{
                          borderColor: "rgba(184,115,51,0.14)",
                          background: "rgba(255,255,255,0.02)",
                        }}
                      >
<div className="flex items-start justify-between gap-2">
  <div className="flex items-start gap-3 min-w-0">
    {occupied ? <SeatAvatarMini seat={s} /> : null}

    <div className="min-w-0">
      <div className="text-xs text-zinc-500">Seat {s.seat}</div>
      <div className="text-zinc-100 font-semibold truncate mt-0.5">
        {occupied ? displayName : "—"}
      </div>
      {occupied ? (
        <div className="text-xs text-zinc-400 mt-1">
          Buy-in: <span className="text-zinc-200 font-semibold">{money(s.buyIn)}</span>
        </div>
      ) : null}
    </div>
  </div>

  {occupied ? (
    <button
      onClick={() => clearSeat(t, s.seat)}
      className="px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
    >
      Remove
    </button>
  ) : null}
</div>

                        {!occupied && (
                          <AssignSeatForm
                            table={t}
                            seatNumber={s.seat}
                            minBuyIn={t.minBuyIn ?? minBuyIn}
                            maxBuyIn={t.maxBuyIn ?? maxBuyIn}
                            fallbackMembers={members}
                            onAssign={(payload) => setSeat(t, s.seat, payload)}
                            excludeUids={excludedUidsForSeatSearch}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}

/**
 * Typeahead seat assignment with exclusion list:
 * - excludeUids: Set of uids that should NOT appear (already seated at other tables)
 * ✅ Now shows avatar in dropdown + selected line
 */
function AssignSeatForm({ table, seatNumber, minBuyIn, maxBuyIn, fallbackMembers, onAssign, excludeUids }) {
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [buyIn, setBuyIn] = useState(minBuyIn);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const blurTimerRef = useRef(null);

  useEffect(() => {
    setBuyIn(minBuyIn);
    setTerm("");
    setSelected(null);
    setResults([]);
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.id, seatNumber, minBuyIn]);

  const safeClose = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  const pick = (m) => {
    setSelected(m);
    setTerm(m?.name ? `${m.name}${m.email ? ` (${m.email})` : ""}` : "");
    setOpen(false);
    setResults([]);
  };

  const localFallbackResults = useMemo(() => {
    const q = String(term || "").trim().toLowerCase();
    if (q.length < 2) return [];
    const list = Array.isArray(fallbackMembers) ? fallbackMembers : [];
    const excluded = excludeUids instanceof Set ? excludeUids : new Set();

    return list
      .filter((m) => {
        const uid = String(m.uid || m.id || "").trim();
        if (!uid) return false;
        if (excluded.has(uid)) return false;

        const name = String(m.name || "").toLowerCase();
        const email = String(m.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 10)
      .map((m) => ({
        uid: m.uid,
        name: m.name,
        email: m.email,
        status: m.status,
        profilePhotoUrl: m.profilePhotoUrl || "",
      }));
  }, [term, fallbackMembers, excludeUids]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const q = String(term || "").trim().toLowerCase();
      if (q.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const usersRef = collection(db, "users");

        const nameQ = query(
          usersRef,
          where("status", "==", "active"),
          orderBy("nameLower", "asc"),
          startAt(q),
          endAt(q + "\uf8ff"),
          limit(10)
        );

        const emailQ = query(
          usersRef,
          where("status", "==", "active"),
          orderBy("emailLower", "asc"),
          startAt(q),
          endAt(q + "\uf8ff"),
          limit(10)
        );

        const [nameSnap, emailSnap] = await Promise.all([getDocs(nameQ), getDocs(emailQ)]);

        const merged = new Map();
        nameSnap.docs.forEach((d) => merged.set(d.id, normalizeMember(d.id, d.data())));
        emailSnap.docs.forEach((d) => merged.set(d.id, normalizeMember(d.id, d.data())));

        const excluded = excludeUids instanceof Set ? excludeUids : new Set();

        const out = Array.from(merged.values())
          .filter((m) => !excluded.has(String(m.uid || "").trim()))
          .slice(0, 10)
          .map((m) => ({
            uid: m.uid,
            name: m.name,
            email: m.email,
            status: m.status,
            profilePhotoUrl: m.profilePhotoUrl || "",
          }));

        if (!alive) return;
        setResults(out);
      } catch {
        if (!alive) return;
        setResults(localFallbackResults);
      } finally {
        if (alive) setLoading(false);
      }
    };

    const t = setTimeout(run, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [term, localFallbackResults, excludeUids]);

  const shownResults = results.length ? results : localFallbackResults;
  const canSeat = !!selected?.uid;

  return (
    <div className="mt-3 grid grid-cols-1 gap-2">
      <label className="text-xs text-zinc-500">Search active member</label>

      <div className="relative">
        <input
          type="text"
          className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
          placeholder="Type name or email (min 2 chars)"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setSelected(null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={safeClose}
        />

        {open && String(term || "").trim().length >= 2 && (
          <div className="absolute z-50 mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl overflow-hidden">
            <div className="px-3 py-2 text-xs text-zinc-500 border-b border-zinc-800">{loading ? "Searching..." : "Select a member"}</div>

            {shownResults.length === 0 ? (
              <div className="px-3 py-3 text-sm text-zinc-400">No matches.</div>
            ) : (
              <ul className="max-h-56 overflow-auto">
                {shownResults.map((m) => (
                  <li key={m.uid}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-zinc-900 text-zinc-100"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(m)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <TinyAvatar name={m.name} url={m.profilePhotoUrl} size={26} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{m.name}</div>
                          {m.email ? <div className="text-xs text-zinc-400 truncate">{m.email}</div> : null}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
          value={buyIn}
          min={minBuyIn}
          max={maxBuyIn}
          step={1}
          onChange={(e) => setBuyIn(e.target.value)}
        />

        <button
          onClick={() =>
            onAssign({
              memberUid: selected?.uid,
              memberLabel: selected?.name ? `${selected.name}${selected.email ? ` (${selected.email})` : ""}` : "",
              buyIn,
              profilePhotoUrl: selected?.profilePhotoUrl || "", // ✅ ADD THIS
            })
          }
          disabled={!canSeat}
          className={`px-3 py-2 rounded text-sm font-semibold ${
            canSeat ? "bg-green-700 hover:bg-green-800 text-white" : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
          }`}
          title={canSeat ? "Seat this member" : "Select a member first"}
        >
          Seat
        </button>
      </div>

      <div className="text-xs text-zinc-500">Buy-in allowed: {money(minBuyIn)}–{money(maxBuyIn)} (step $1)</div>

      {selected?.uid ? (
        <div className="text-xs text-zinc-400 flex items-center gap-2">
          <TinyAvatar name={selected.name} url={selected.profilePhotoUrl} size={20} />
          <div className="min-w-0">
            Selected: <span className="text-zinc-200 font-semibold">{selected.name}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}